<template>
  <section class="scenarios">
    <div class="container">
      <div class="title-area reveal">
        <h2 class="section-title">See Bizzento in Action</h2>
        <p class="section-subtitle">
          From instant execution to long-term strategy.
        </p>
      </div>

      <div class="scenarios-grid">
        <div class="scenario-card ready-card reveal">
          <div class="status-badge ready">
            <span class="dot-pulse"></span> Ready Now
          </div>
          <div class="card-content">
            <div class="text-side">
              <span class="card-num">01</span>
              <h3>Daily Operations</h3>
              <p>
                Watch Bizzento bridge the gap between a customer inquiry and a
                confirmed booking without you lifting a finger.
              </p>
            </div>
            <div class="visual-side">
              <div class="gif-window">
                <div class="window-controls">
                  <span></span><span></span><span></span>
                </div>
                <img
                  src="../assets/images/pabu-daily-operations.gif"
                  alt="Daily Operations"
                  class="scenario-gif"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="beta-row">
          <div class="scenario-card beta-card reveal delay-1">
            <div class="status-badge beta">🔧 Beta: Help Us Build It</div>
            <div class="card-content">
              <h3>Business Intelligence</h3>
              <p>
                Transform raw data into visual growth charts and actionable
                insights.
              </p>
              <div class="gif-window mini">
                <img
                  src="../assets/images/pabu-business-intelligence.gif"
                  alt="Intelligence"
                  class="scenario-gif"
                />
              </div>
            </div>
          </div>

          <div class="scenario-card beta-card reveal delay-2">
            <div class="status-badge beta">🔧 Beta: Help Us Build It</div>
            <div class="card-content">
              <h3>Strategic Guidance</h3>
              <p>
                Market analysis and revenue projections to guide your next big
                move.
              </p>
              <div class="gif-window mini">
                <img
                  src="../assets/images/pabu-strategic-guidance.gif"
                  alt="Strategic"
                  class="scenario-gif"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.scenarios {
  /* Theme Variables */
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;
  --success-green: #2d6a4f;

  padding: 100px 24px;
  background: var(--bg-warm-base);
  color: var(--dark-accent);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.title-area {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 12px;
}

.section-subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
}

.scenarios-grid {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Base Card Styling */
.scenario-card {
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 32px;
  padding: 40px;
  position: relative;
  overflow: hidden;
}

/* Status Badges */
.status-badge {
  position: absolute;
  top: 24px;
  right: 24px;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ready {
  background: #e7f3ee;
  color: var(--success-green);
}
.beta {
  background: var(--sunlight-glow);
  color: #856404;
  border: 1px solid var(--soft-gold);
}

/* Main Feature Card (Scenario 1) */
.ready-card {
  background: white;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.03);
}

.ready-card .card-content {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 40px;
  align-items: center;
}

.card-num {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--soft-gold);
  letter-spacing: 2px;
  display: block;
  margin-bottom: 12px;
}

/* Beta Row Styling */
.beta-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

/* Window Mockup Look */
.gif-window {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #eee;
}

.window-controls {
  background: #f8f8f8;
  padding: 10px;
  display: flex;
  gap: 6px;
}

.window-controls span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ddd;
}

.scenario-gif {
  width: 100%;
  display: block;
  transition: transform 0.5s ease;
}

.scenario-card:hover .scenario-gif {
  transform: scale(1.02);
}

/* Mobile Responsiveness */
@media (max-width: 992px) {
  .ready-card .card-content {
    grid-template-columns: 1fr;
  }
  .beta-row {
    grid-template-columns: 1fr;
  }
  .scenario-card {
    padding: 30px;
  }
}

/* Animation for the "Ready" dot */
.dot-pulse {
  width: 8px;
  height: 8px;
  background: var(--success-green);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(45, 106, 79, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(45, 106, 79, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(45, 106, 79, 0);
  }
}
</style>
<script>
export default {
  name: 'ScenariosSection',
  /*
  data() {
    return {
      image1: require("@/assets/images/abouts-1.jpg"),
      image2: require("@/assets/images/abouts-2.jpg"),
      graphic1: "https://csimg.nyc3.cdn.digitaloceanspaces.com/Images/Graphics/splash-4.svg",
      graphic2: "https://csimg.nyc3.cdn.digitaloceanspaces.com/Images/Graphics/tire-mark.svg",
      features: [
        "Daily Personal Activities & Assistance",
        "Travel & Transport Support",
        "Community & Social Participation",
        "Household Task Assistance",
        "Community Nursing Care"
      ]
    };
  }*/
};
</script>
